import * as React from 'react';
import { memo } from 'react';

import { ColumnType } from 'helper/converter/measurement-diagrams-converter/measurement-diagrams-converter';
import { generateClassNameString } from 'helper/css';

export type ChartColumn = {
	value: null | number;
	label: string;
};

export type ChartModifierType = 'year';

export type ChartProps = {
	max: number;
	mid: null | number;
	min: number;
	modifier?: ChartModifierType;
	isLoading?: boolean;
	columns: ChartColumn[];
	line?: string;
	tooltips?: boolean;
};

export type ChartState = {
	activeTooltip?: string;
};

export type SvgValuesType = {
	start: number | typeof NaN;

	end: number;

	colSpan: number;
};

/*
 * see unit tests for details
 */
export function getSvgValues(values: Array<null | number>, index: number): SvgValuesType | undefined {
	if (index === 0) {
		return;
	}

	let colSpan = 1;

	const getPrevValue = (i: number) => {
		if (values[i - 1] === null) {
			colSpan += 1;

			return getPrevValue(i - 1);
		}

		return values[i - 1];
	};

	if (values[index] === null) {
		return void 0;
	}

	const value = values[index]!;
	const prevValue = getPrevValue(index);

	return {
		start: prevValue === null ? NaN : 100 - prevValue,
		end: 100 - value,
		colSpan,
	};
}

export class Chart extends React.Component<ChartProps, ChartState> {
	public static displayName = 'Chart';

	public state: ChartState = {
		activeTooltip: undefined,
	};

	public handleToggleTooltip = (reactKey: string) => {
		if (this.state.activeTooltip === reactKey) {
			this.setState({
				activeTooltip: undefined,
			});
			return;
		}
		this.setState({ activeTooltip: reactKey });
	};

	public valueAsPercent(value: null | number): null | number {
		if (value === null) {
			return null;
		}
		return ((value - this.props.min) * 100) / (this.props.max - this.props.min);
	}

	get chartClassName(): string {
		const { isLoading = false, modifier } = this.props;
		const className: Array<string> = [generateClassNameString('m-chart', modifier)];
		if (isLoading) {
			className.push('state-m-chart--loading');
		}
		return className.join(' ');
	}

	get roundedMid(): number {
		return Math.round(parseFloat(`${this.props.mid}`) * 10) / 10;
	}

	get viewBoxWidth(): 50 | 25 {
		if (this.props.columns.length > 6) {
			return 25;
		}
		return 50;
	}

	public getRoundedTooltipValue(value: null | number): string {
		if (!value) {
			return '';
		}

		return (Math.round(parseFloat(`${value}`) * 10) / 10).toString();
	}

	public render() {
		const { activeTooltip } = this.state;
		const { max, mid, min, columns, line, tooltips = false } = this.props;
		const percentDesiredWeight = this.valueAsPercent(mid);

		return (
			<div className={this.chartClassName}>
				<div className="m-chart__wrapper">
					<div className="m-chart__values">
						<span className="m-chart__values-text">{max}</span>
						{percentDesiredWeight ? (
							<span className="m-chart__values-text" style={{ top: `${100 - percentDesiredWeight}%` }}>
								{this.roundedMid}
							</span>
						) : (
							<span className="m-chart__values-text" />
						)}
						<span className="m-chart__values-text">{min}</span>
					</div>
					<div className="m-chart__graph">
						<div className="m-chart__columns">
							{columns &&
								columns.map(({ label, value }: ColumnType, index: number) => {
									const svgValues = getSvgValues(
										columns.map((column) => this.valueAsPercent(column.value)),
										index
									);
									const reactKey = `${label}-${value || 'null'}-${index}`;
									const percentValue = this.valueAsPercent(value);

									return (
										<div key={reactKey} className="m-chart__column">
											<div className="m-chart__column-inner">
												{percentValue !== null && (
													<span
														className={`m-chart__dot${
															tooltips ? ' m-chart__dot--tooltip' : ''
														}`}
														style={{ bottom: `${percentValue}%` }}
														onClick={this.handleToggleTooltip.bind(this, reactKey)}
													>
														{tooltips && (
															<span
																className={`m-chart__tooltip${
																	activeTooltip === reactKey
																		? ' state-m-chart__tooltip--visible'
																		: ''
																}`}
															>
																{this.getRoundedTooltipValue(value)}
															</span>
														)}
													</span>
												)}
												{svgValues && !isNaN(svgValues.start) && (
													<div
														className={`m-chart__connector m-chart__connector--col${svgValues.colSpan}`}
													>
														<svg
															version="1.1"
															viewBox={`0 0 ${svgValues.colSpan * this.viewBoxWidth} 100`}
															preserveAspectRatio="none"
														>
															<path
																d={`M0,${svgValues.start} L${svgValues.colSpan *
																	this.viewBoxWidth},${svgValues.end}`}
															/>
														</svg>
													</div>
												)}
											</div>
											{label ? <span className="m-chart__label">{label}</span> : null}
										</div>
									);
								})}
							{line && percentDesiredWeight !== null && (
								<span className="m-chart__line" style={{ top: `${100 - percentDesiredWeight}%` }}>
									<span className="m-chart__line-text">{line}</span>
								</span>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}
}

Chart.displayName = 'Chart';

export default memo(Chart);
